import { type FC } from 'react';
import { useAuiState, useThread } from '@assistant-ui/react';
import {
  ArrowUpRightIcon,
  BookOpenIcon,
  BrainIcon,
  EyeIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  PanelRightOpenIcon,
  ZapIcon,
} from 'lucide-react';
import { useFiles } from '@/client/lib/filesContext';
import { useExternalAssets } from '@/client/lib/assetsContext';
import { useOptionalNavigation } from '@/client/app/providers/navigation-context';
import { useOptionalAuth } from '@/client/app/providers/auth-context';
import { Button } from '@/client/components/ui/button';
import { cn } from '@/client/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/client/components/ui/tooltip';

/* ─── Helpers ────────────────────────────────────────────────────── */

function getFileExtBadge(path: string): string {
  const ext = path.split('.').pop()?.toUpperCase() || 'FILE';
  return ext;
}

function getFileName(path: string): string {
  const name = path.split('/').pop() ?? path;
  const withoutExt = name.replace(/\.[^.]+$/, '');
  return withoutExt
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseToolResult(result: unknown): Record<string, unknown> | null {
  if (!result) return null;
  if (typeof result === 'string') {
    try { return JSON.parse(result); } catch { return null; }
  }
  if (typeof result === 'object' && !Array.isArray(result)) return result as Record<string, unknown>;
  return null;
}

/* ─── File Card (filesystem files) ───────────────────────────────── */

const FileCard: FC<{ filePath: string }> = ({ filePath }) => {
  const { setSelectedFile } = useFiles();
  const { setPanelCollapsed, setPanelTab } = useExternalAssets();

  const handleView = () => {
    setSelectedFile(filePath);
    setPanelCollapsed(false);
    setPanelTab('files');
  };

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div className="flex items-center gap-2 px-3 py-2">
        <FileTextIcon className="size-4 flex-shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{getFileName(filePath)}</span>
        <span className="flex-shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
          {getFileExtBadge(filePath)}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="size-7" onClick={handleView}>
              <PanelRightOpenIcon className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">View in panel</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};

/* ─── Resource Card (skill / guidance / action) ──────────────────── */

type ResourceKind = 'skill' | 'guidance' | 'action';

const RESOURCE_META: Record<
  ResourceKind,
  { label: string; icon: typeof BrainIcon; pillClass: string }
> = {
  skill: {
    label: 'Skill',
    icon: BrainIcon,
    pillClass: 'bg-primary/10 text-primary',
  },
  guidance: {
    label: 'Guidance',
    icon: BookOpenIcon,
    pillClass: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  },
  action: {
    label: 'Action',
    icon: ZapIcon,
    pillClass: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
};

const SKILL_TOOLS = new Set([
  'get_skill',
  'create_draft_skill',
  'update_draft_skill',
  'publish_skill',
]);
const GUIDANCE_TOOLS = new Set([
  'get_guidance',
  'create_draft_guidance',
  'update_draft_guidance',
  'publish_guidance',
]);
const ACTION_TOOLS = new Set([
  'get_support_action',
  'create_support_action',
  'update_support_action',
  'create_action_from_template',
  'enable_support_action',
  'disable_support_action',
  'convert_to_advanced_view',
]);

function classifyResourceTool(toolName: string): ResourceKind | null {
  if (SKILL_TOOLS.has(toolName)) return 'skill';
  if (GUIDANCE_TOOLS.has(toolName)) return 'guidance';
  if (ACTION_TOOLS.has(toolName)) return 'action';
  return null;
}

function extractResourceKeysFromParts(parts: readonly unknown[] | undefined): string[] {
  if (!parts) return [];
  const keys: string[] = [];
  for (const part of parts) {
    const p = part as { type?: string; toolName?: string; result?: unknown };
    if (p.type !== 'tool-call' || !p.toolName) continue;
    const kind = classifyResourceTool(p.toolName);
    if (!kind) continue;
    const result = parseToolResult(p.result);
    const id = result?.id as string | number | undefined;
    if (id === undefined || id === null) continue;
    keys.push(`resource:${kind}:${id}`);
  }
  return keys;
}

/**
 * Collect resource keys from every thread message strictly before the current one.
 * Used to dedupe resource cards across turns so the same skill/guidance/action
 * is surfaced only on its first mention in the thread.
 */
function useResourceKeysBeforeCurrentMessage(): Set<string> {
  const threadMessages = useThread((s) => s.messages) as ReadonlyArray<{
    id: string;
    parts?: readonly unknown[];
  }>;
  const currentMessageId = useAuiState((s) => s.message.id);
  const seen = new Set<string>();
  if (!threadMessages || !currentMessageId) return seen;
  for (const msg of threadMessages) {
    if (msg.id === currentMessageId) break;
    for (const key of extractResourceKeysFromParts(msg.parts)) seen.add(key);
  }
  return seen;
}

function useStoreSlug(): string | null {
  const auth = useOptionalAuth();
  const pageUrl = auth?.pageUrl;
  if (!pageUrl) return null;
  const match = pageUrl.match(/\/shopify\/([^/?#]+)/);
  return match?.[1] ?? null;
}

function useCurrentPagePath(): string | null {
  const auth = useOptionalAuth();
  const pageUrl = auth?.pageUrl;
  if (!pageUrl) return null;
  try {
    return new URL(pageUrl, window.location.origin).pathname;
  } catch {
    return pageUrl.split('?')[0].split('#')[0];
  }
}

function buildResourceUrl(kind: ResourceKind, storeSlug: string, id: string | number): string {
  const base = `/app/ai-agent/shopify/${storeSlug}`;
  switch (kind) {
    case 'skill':
      return `${base}/skills/${id}`;
    case 'guidance':
      return `${base}/knowledge/guidance/${id}`;
    case 'action':
      return `${base}/actions/edit/${id}`;
  }
}

function isViewingPath(currentPath: string | null, targetPath: string | null): boolean {
  if (!currentPath || !targetPath) return false;
  const normalize = (p: string) => p.replace(/\/+$/, '');
  const c = normalize(currentPath);
  const t = normalize(targetPath);
  return c === t || c.startsWith(`${t}/`);
}

const ResourceCard: FC<{ resourceKind: ResourceKind; id: string | number; title: string }> = ({
  resourceKind,
  id,
  title,
}) => {
  const storeSlug = useStoreSlug();
  const currentPath = useCurrentPagePath();
  const meta = RESOURCE_META[resourceKind];
  const Icon = meta.icon;

  const targetUrl = storeSlug ? buildResourceUrl(resourceKind, storeSlug, id) : null;
  const isViewing = isViewingPath(currentPath, targetUrl);

  const handleView = () => {
    if (!targetUrl || isViewing) return;
    window.parent.postMessage({ type: 'GAIA_EMBED_AGENT_DONE' }, '*');
    setTimeout(() => {
      window.parent.postMessage({ type: 'GAIA_EMBED_NAVIGATE', url: targetUrl }, '*');
    }, 100);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border bg-background px-3.5 py-3 transition-colors',
        isViewing && 'border-primary ring-1 ring-primary/20',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium leading-none',
            meta.pillClass,
          )}
        >
          <Icon className="size-3.5" />
          {meta.label}
        </span>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2.5 text-xs"
          onClick={handleView}
          disabled={!targetUrl || isViewing}
          aria-current={isViewing ? 'page' : undefined}
        >
          {isViewing ? (
            'Viewing'
          ) : (
            <>
              View
              <ArrowUpRightIcon className="size-3" />
            </>
          )}
        </Button>
      </div>
      <div className="line-clamp-2 text-sm font-semibold leading-snug">{title}</div>
    </div>
  );
};

/* ─── Export Card (GCS exported files) ───────────────────────────── */

const ExportCard: FC<{ title: string; objectPath: string; contentType: string }> = ({
  title,
  objectPath,
  contentType,
}) => {
  const appNav = useOptionalNavigation();

  const handleView = () => {
    if (appNav && objectPath) {
      appNav.navigateToDocument(objectPath);
    }
  };

  const Icon = contentType === 'csv' ? FileSpreadsheetIcon : FileTextIcon;

  return (
    <div className="overflow-hidden rounded-lg border bg-background">
      <div className="flex items-center gap-2 px-3 py-2">
        <Icon className="size-4 flex-shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{title}</span>
        <span className="flex-shrink-0 rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
          {contentType.toUpperCase() || 'FILE'}
        </span>
        {appNav && objectPath && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="size-7" onClick={handleView}>
                <EyeIcon className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">View in Documents</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

/* ─── MessageArtifacts ───────────────────────────────────────────── */

type CardItem =
  | { kind: 'file'; path: string }
  | { kind: 'export'; title: string; objectPath: string; contentType: string }
  | { kind: 'resource'; resourceKind: ResourceKind; id: string | number; title: string };

export const MessageArtifacts: FC = () => {
  const parts = useAuiState((s) => s.message.parts);
  const priorResourceKeys = useResourceKeysBeforeCurrentMessage();

  const cards: CardItem[] = [];

  if (parts) {
    for (const part of parts) {
      if (part.type !== 'tool-call') continue;
      const tc = part as {
        toolName: string;
        argsText?: string;
        result?: unknown;
        status?: { type: string };
      };
      if (tc.status?.type === 'running') continue;

      // Filesystem file cards (write_file, edit_file)
      if (tc.toolName === 'write_file' || tc.toolName === 'edit_file') {
        try {
          const args = tc.argsText ? JSON.parse(tc.argsText) : {};
          const path = args.path || args.file_path;
          if (path && typeof path === 'string' && path.startsWith('/shared/')) {
            cards.push({ kind: 'file', path });
          }
        } catch { /* skip */ }
        continue;
      }

      // Export cards (export_file, export_data) — parse result JSON
      if (tc.toolName === 'export_file' || tc.toolName === 'export_data') {
        const result = parseToolResult(tc.result);
        if (result?.success) {
          const filename = (result.filename as string) || '';
          // object_path from result, or extract from uuid (format: "gcs:<path>")
          const objectPath = (result.object_path as string)
            || ((result.uuid as string) || '').replace(/^gcs:/, '')
            || '';
          const contentType = (result.content_type as string) || '';
          cards.push({
            kind: 'export',
            title: getFileName(filename || objectPath),
            objectPath,
            contentType,
          });
        }
        continue;
      }

      // Resource cards (skills, guidance, support actions)
      const resourceKind = classifyResourceTool(tc.toolName);
      if (resourceKind) {
        const result = parseToolResult(tc.result);
        const id = result?.id as string | number | undefined;
        // skills/guidance return `title`; support actions return `name`
        const title = (result?.title as string | undefined) ?? (result?.name as string | undefined);
        if (id !== undefined && id !== null && title) {
          cards.push({ kind: 'resource', resourceKind, id, title });
        }
        continue;
      }
    }
  }

  if (cards.length === 0) return null;

  // Deduplicate, keeping the LAST occurrence per key so resource cards reflect
  // the final state when the same resource is touched by multiple tool calls
  // in a single turn (e.g. create → update with the same id).
  const keyFor = (c: CardItem): string => {
    switch (c.kind) {
      case 'file':
        return `file:${c.path}`;
      case 'export':
        return `export:${c.objectPath}`;
      case 'resource':
        return `resource:${c.resourceKind}:${c.id}`;
    }
  };
  const byKey = new Map<string, CardItem>();
  for (const card of cards) {
    const key = keyFor(card);
    if (!key || key.endsWith(':')) continue;
    // Thread-level dedupe for resource cards: drop if the same (kind, id)
    // already appeared in an earlier message.
    if (card.kind === 'resource' && priorResourceKeys.has(key)) continue;
    byKey.set(key, card);
  }
  const uniqueCards = [...byKey.values()];

  if (uniqueCards.length === 0) return null;

  return (
    <div className="mt-2 space-y-2 px-2">
      {uniqueCards.map((card, i) => {
        switch (card.kind) {
          case 'file':
            return <FileCard key={card.path} filePath={card.path} />;
          case 'export':
            return (
              <ExportCard
                key={card.objectPath || i}
                title={card.title}
                objectPath={card.objectPath}
                contentType={card.contentType}
              />
            );
          case 'resource':
            return (
              <ResourceCard
                key={`${card.resourceKind}:${card.id}`}
                resourceKind={card.resourceKind}
                id={card.id}
                title={card.title}
              />
            );
        }
      })}
    </div>
  );
};
